import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, delay, Observable, of, throwError } from 'rxjs';
import {
  PaymentDocument,
  SettlementAcceptance,
  SettlementCancellation,
  SettlementLink,
} from '../models/settlement-action.model';
import { Settlement } from '../models/settlement.model';
import { markPaymentDocumentLinked, mockPaymentDocuments } from './payment-document.fixture';
import { MockSettlementRecord, mockSettlementRecords } from './settlement.fixture';
import { SettlementActionRepository } from './settlement-action.repository';

const MOCK_LATENCY_MS = 400;
const AMOUNT_TOLERANCE = 1;

const reject = (status: number, message: string, details?: unknown) =>
  throwError(
    () =>
      new HttpErrorResponse({
        status,
        error: {
          success: false,
          error: { code: status === 409 ? 'CONFLICT' : 'UNPROCESSABLE_ENTITY', message, details },
        },
      }),
  );

const wrongStatus = ({ statusId, status }: Settlement) =>
  reject(409, `La liquidación está en estado ${status}.`, {
    currentStatusId: statusId,
    currentStatus: status,
  });

const setStatus = (settlement: Settlement, statusId: number, status: string): void => {
  settlement.statusId = statusId;
  settlement.status = status;
};

// El BFF aún no expone validar/anular/vincular para el portal (CA-787); la API ya los tiene.
@Injectable()
export class SettlementActionMockRepository extends SettlementActionRepository {
  override validate(settlementId: number): Observable<SettlementAcceptance> {
    return this.withRecord(settlementId, ({ settlement, bonuses }) => {
      if (settlement.statusId !== 2) return wrongStatus(settlement);
      if (!settlement.validBonuses) return reject(422, 'La liquidación no tiene bonos válidos.');
      const discarded = bonuses.filter((bonus) => bonus.statusId !== 1);
      discarded.forEach((bonus) => Object.assign(bonus, { statusId: 3, status: 'ANULADO' }));
      setStatus(settlement, 3, 'POR VINCULAR');
      return of({
        settlementId,
        statusId: 3,
        status: 'POR VINCULAR',
        totalAmount: settlement.totalAmount ?? 0,
        acceptedBonds: settlement.validBonuses,
        discardedBonds: discarded.length,
      });
    });
  }

  override cancel(settlementId: number): Observable<SettlementCancellation> {
    return this.withRecord(settlementId, ({ settlement, bonuses }) => {
      if (settlement.statusId !== 2 && settlement.statusId !== 3) return wrongStatus(settlement);
      bonuses.forEach((bonus) => Object.assign(bonus, { statusId: 3, status: 'ANULADO' }));
      setStatus(settlement, 7, 'ANULADA');
      settlement.totalAmount = null;
      return of({
        settlementId,
        statusId: 7,
        status: 'ANULADA',
        cancelledBonds: bonuses.length,
        unlinkedDocuments: 0,
      });
    });
  }

  override listPaymentDocuments(settlementId: number): Observable<PaymentDocument[]> {
    return this.withRecord(settlementId, ({ settlement }) =>
      settlement.statusId === 3 ? of(mockPaymentDocuments(settlement)) : wrongStatus(settlement),
    );
  }

  override link(settlementId: number, dteId: number): Observable<SettlementLink> {
    return this.withRecord(settlementId, ({ settlement }) => {
      if (settlement.statusId !== 3) return wrongStatus(settlement);
      const document = mockPaymentDocuments(settlement).find((item) => item.dteId === dteId);
      if (!document) {
        return reject(422, 'El DTE seleccionado ya no está disponible.', {
          dteId,
          reason: 'DTE_NOT_FOUND',
        });
      }
      if (
        Math.abs((document.totalAmount ?? 0) - (settlement.totalAmount ?? 0)) > AMOUNT_TOLERANCE
      ) {
        return reject(422, 'El monto del DTE no coincide con el total de la liquidación.', {
          dteId,
          reason: 'DTE_AMOUNT_MISMATCH',
        });
      }
      markPaymentDocumentLinked(dteId);
      setStatus(settlement, 4, 'EN PROCESO DE PAGO');
      settlement.linkDate = new Date().toISOString();
      return of({
        settlementId,
        statusId: 4,
        status: 'EN PROCESO DE PAGO',
        opaNumber: null,
        opaPaymentDate: null,
        linkDate: settlement.linkDate,
        linkedDtes: [dteId],
      });
    });
  }

  private withRecord<T>(
    settlementId: number,
    action: (record: MockSettlementRecord) => Observable<T>,
  ): Observable<T> {
    return defer(() => {
      const record = mockSettlementRecords.find((r) => r.settlement.settlementId === settlementId);
      return record ? action(record) : throwError(() => new HttpErrorResponse({ status: 404 }));
    }).pipe(delay(MOCK_LATENCY_MS));
  }
}
